import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  HelpCircle, 
  Search,
  Scale,
  FileText,
  Shield,
  Users
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  general: HelpCircle,
  procedures: FileText,
  droits: Shield,
  defense: Scale,
  communaute: Users
};

const categoryLabels = {
  general: 'Questions générales',
  procedures: 'Procédures',
  droits: 'Vos droits',
  defense: 'Défense',
  communaute: 'Communauté'
};

export default function FAQ() {
  const [faqs, setFaqs] = useState([]);
  const [filteredFaqs, setFilteredFaqs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFaqs();
  }, []);

  useEffect(() => {
    if (!searchQuery) {
      setFilteredFaqs(faqs);
    } else {
      const query = searchQuery.toLowerCase();
      setFilteredFaqs(faqs.filter(faq => 
        faq.question.toLowerCase().includes(query) || 
        faq.answer.toLowerCase().includes(query)
      ));
    }
  }, [faqs, searchQuery]);

  const fetchFaqs = async () => {
    try {
      const response = await axios.get(`${API}/faq`);
      setFaqs(response.data);
      setFilteredFaqs(response.data);
    } catch (error) {
      console.error('Failed to fetch FAQs:', error);
    } finally {
      setLoading(false);
    }
  };

  const groupedFaqs = filteredFaqs.reduce((acc, faq) => {
    if (!acc[faq.category]) {
      acc[faq.category] = [];
    }
    acc[faq.category].push(faq);
    return acc;
  }, {});

  return (
    <div className="min-h-screen" data-testid="faq-page">
      {/* Header */}
      <div className="bg-muted/30 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
            Questions fréquentes
          </h1>
          <p className="text-muted-foreground">
            Réponses aux questions les plus courantes sur l'habitat léger et vos droits.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search */}
        <Card className="mb-8">
          <CardContent className="py-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher une question..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="faq-search"
              />
            </div>
          </CardContent>
        </Card>

        {/* FAQs */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="py-4">
                  <div className="h-4 bg-muted rounded w-3/4" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredFaqs.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <HelpCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">Aucune question trouvée</h3>
              <p className="text-muted-foreground">
                Essayez avec d'autres termes de recherche ou posez votre question à notre assistant IA.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedFaqs).map(([category, categoryFaqs]) => {
              const Icon = categoryIcons[category] || HelpCircle;
              return (
                <div key={category}>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 rounded-sm bg-primary/10 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-primary" />
                    </div>
                    <h2 className="text-xl font-semibold">
                      {categoryLabels[category] || category}
                    </h2>
                    <Badge variant="secondary">{categoryFaqs.length}</Badge>
                  </div>
                  <Card>
                    <CardContent className="p-0">
                      <Accordion type="single" collapsible className="w-full">
                        {categoryFaqs.map((faq, index) => (
                          <AccordionItem 
                            key={faq.id} 
                            value={faq.id}
                            className={index === categoryFaqs.length - 1 ? 'border-b-0' : ''}
                            data-testid={`faq-item-${faq.id}`}
                          >
                            <AccordionTrigger className="px-6 text-left hover:no-underline">
                              <span className="font-medium">{faq.question}</span>
                            </AccordionTrigger>
                            <AccordionContent className="px-6 pb-4">
                              <p className="text-muted-foreground whitespace-pre-wrap">
                                {faq.answer}
                              </p>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        )}

        {/* CTA */}
        <Card className="mt-12">
          <CardHeader>
            <CardTitle>Vous n'avez pas trouvé votre réponse ?</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground mb-4">
              Notre assistant IA peut répondre à vos questions spécifiques 
              ou vous orienter vers les bonnes ressources.
            </p>
            <a href="/assistant">
              <button className="inline-flex items-center justify-center whitespace-nowrap rounded-sm text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2">
                Poser ma question à l'assistant
              </button>
            </a>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
